import openai from '../lib/openai';
import { APIConnectionError, AuthenticationError, PermissionDeniedError, RateLimitError, InternalServerError } from 'openai';
import { categoryService } from './categoryService';
import { transactionService } from './transactionService';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const aiCategorizationService = {
  /**
   * Suggests a category for a transaction based on merchant data and user patterns.
   * @param {string} description - Transaction description (merchant name/data).
   * @param {string} transactionType - Transaction type (income/expense/transfer).
   * @param {number} amount - Transaction amount.
   * @returns {Promise<Object>} Suggested category with confidence score.
   */
  async suggestCategory(description, transactionType, amount) {
    try {
      // Get all available categories for the user
      const allCategories = await categoryService?.getAll();
      
      // Filter categories by transaction type
      const relevantCategories = allCategories?.filter(cat => cat?.type === transactionType);
      
      if (!relevantCategories || relevantCategories?.length === 0) {
        return {
          suggestedCategory: null,
          confidence: 0,
          reasoning: 'No categories available for this transaction type'
        };
      }

      // Get user's transaction history for pattern learning
      const historicalTransactions = await transactionService?.getAll();
      
      // Build pattern context from historical data
      const patternContext = this.buildPatternContext(historicalTransactions, description);

      // Create category list for AI
      const categoryList = relevantCategories?.map(cat => `${cat?.name} (${cat?.type})`)?.join(', ');

      // Build AI prompt with structured output
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: `You are a financial transaction categorization expert. Analyze merchant data and transaction patterns to suggest the most appropriate category. Consider:
1. Merchant name patterns (e.g., "Starbucks" → Food & Dining)
2. Transaction type (${transactionType})
3. Amount context ($${Math.abs(amount)})
4. User's historical categorization patterns

Available categories: ${categoryList}

${patternContext ? `User patterns: ${patternContext}` : ''}`
          },
          {
            role: 'user',
            content: `Categorize this transaction:
Description: "${description}"
Type: ${transactionType}
Amount: $${Math.abs(amount)}

Provide the best matching category name and explain your reasoning.`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'category_suggestion',
            schema: {
              type: 'object',
              properties: {
                suggestedCategory: { type: 'string', description: 'Exact category name from the available list' },
                confidence: { type: 'number', description: 'Confidence score between 0 and 1' },
                reasoning: { type: 'string', description: 'Brief explanation of why this category was chosen' }
              },
              required: ['suggestedCategory', 'confidence', 'reasoning'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'minimal',
        verbosity: 'low'
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);

      // Find the matching category object
      const matchedCategory = relevantCategories?.find(
        cat => cat?.name?.toLowerCase() === result?.suggestedCategory?.toLowerCase()
      );

      return {
        categoryId: matchedCategory?.id || null,
        categoryName: matchedCategory?.name || result?.suggestedCategory,
        confidence: result?.confidence || 0,
        reasoning: result?.reasoning || ''
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in AI categorization:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Builds pattern context from user's historical transactions.
   * @param {Array} transactions - Historical transactions.
   * @param {string} currentDescription - Current transaction description.
   * @returns {string} Pattern context for AI.
   */
  buildPatternContext(transactions, currentDescription) {
    if (!transactions || transactions?.length === 0) return '';

    // Find similar transactions based on description
    const similarTransactions = transactions
      ?.filter(t => {
        const desc = t?.description?.toLowerCase() || '';
        const current = currentDescription?.toLowerCase() || '';
        // Check for partial matches
        return desc?.includes(current?.split(' ')?.[0]) || current?.includes(desc?.split(' ')?.[0]);
      })
      ?.slice(0, 5); // Limit to 5 most recent similar transactions

    if (similarTransactions?.length === 0) return '';

    // Build pattern summary
    const patterns = similarTransactions
      ?.map(t => `"${t?.description}" → ${t?.category}`)
      ?.join('; ');

    return `Similar past transactions: ${patterns}`;
  },

  /**
   * Batch categorize multiple transactions.
   * @param {Array} transactions - Array of transaction objects with description, type, amount.
   * @returns {Promise<Array>} Array of suggestions.
   */
  async batchSuggestCategories(transactions) {
    try {
      const suggestions = [];
      
      for (const transaction of transactions) {
        const suggestion = await this.suggestCategory(
          transaction?.description,
          transaction?.type,
          transaction?.amount
        );
        suggestions?.push({
          ...transaction,
          ...suggestion
        });
      }
      
      return suggestions;
    } catch (error) {
      console.error('Error in batch categorization:', error);
      throw error;
    }
  }
};